import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Drawer, DrawerContent, DrawerHeader, DrawerTitle } from "./ui/drawer";
import useIsMobile from "../hooks/useIsMobile";

/**
 * Mobile-aware dialog wrapper.
 *
 * Mobile path uses a bottom Drawer. On iOS Safari, the bottom sheet sits on
 * top of the keyboard — `dvh` units shrink with the keyboard on iOS 16.4+,
 * and we hook the VisualViewport API to nudge the content above the keyboard
 * on older iOS versions + to scroll the focused input into view.
 */
export default function ResponsiveDialog({
  open, onOpenChange, title, description, children, contentClassName = "", testId,
}) {
  const isMobile = useIsMobile();
  const [mounted, setMounted] = useState(false);
  const [keyboardOffset, setKeyboardOffset] = useState(0);

  useEffect(() => setMounted(true), []);

  // iOS keyboard avoidance — use VisualViewport API to compute the visible-area
  // delta from the layout viewport. Only runs when the drawer is open.
  useEffect(() => {
    if (!open || !isMobile) { setKeyboardOffset(0); return; }
    const vv = typeof window !== "undefined" ? window.visualViewport : null;
    if (!vv) return;
    const onChange = () => {
      // How much the keyboard is occluding (= layoutViewport - visualViewport)
      const occluded = Math.max(0, window.innerHeight - (vv.height + vv.offsetTop));
      setKeyboardOffset(occluded);
    };
    vv.addEventListener("resize", onChange);
    vv.addEventListener("scroll", onChange);
    onChange();
    return () => {
      vv.removeEventListener("resize", onChange);
      vv.removeEventListener("scroll", onChange);
    };
  }, [open, isMobile]);

  // When a form input gets focus inside the drawer, ensure it scrolls into the
  // *visible* area (not just into the drawer, which can be under the keyboard).
  useEffect(() => {
    if (!open || !isMobile) return;
    const onFocusIn = (e) => {
      const t = e.target;
      if (!t || !["INPUT", "TEXTAREA", "SELECT"].includes(t.tagName)) return;
      // Defer so the keyboard has time to open + visualViewport to settle
      setTimeout(() => {
        try { t.scrollIntoView({ block: "center", behavior: "smooth" }); } catch (_) { /* ignore */ }
      }, 250);
    };
    document.addEventListener("focusin", onFocusIn);
    return () => document.removeEventListener("focusin", onFocusIn);
  }, [open, isMobile]);

  if (!mounted) return null;

  if (isMobile) {
    // iOS Safari pre-16.4 doesn't shrink `dvh` with the keyboard, so we add
    // bottom padding equal to the keyboard occlusion. The focused input's
    // `scrollIntoView` above then places it cleanly above the keyboard.
    // We can't transform the whole DrawerContent — Vaul uses transform for
    // its slide-in animation and we'd fight it.
    const innerPad = keyboardOffset > 0
      ? { paddingBottom: `calc(${keyboardOffset}px + env(safe-area-inset-bottom, 0px) + 1rem)` }
      : undefined;
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent
          className={`max-h-[92dvh] ${contentClassName}`}
          data-testid={testId}
        >
          {title && (
            <DrawerHeader className="text-left">
              <DrawerTitle className="font-display text-lg">{title}</DrawerTitle>
              {description && <div className="text-xs text-muted-foreground mt-1">{description}</div>}
            </DrawerHeader>
          )}
          <div
            className="px-4 pb-[max(env(safe-area-inset-bottom),1.5rem)] overflow-y-auto overscroll-contain"
            style={{ WebkitOverflowScrolling: "touch", ...(innerPad || {}) }}
          >
            {children}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className={`sm:max-w-md p-0 gap-0 rounded-3xl overflow-hidden ${contentClassName}`} data-testid={testId}>
        {title && (
          <DialogHeader className="p-5 pb-3">
            <DialogTitle className="font-display text-lg">{title}</DialogTitle>
            <DialogDescription className="text-xs">
              {description || "Action panel"}
            </DialogDescription>
          </DialogHeader>
        )}
        <div className="px-5 pb-5">{children}</div>
      </DialogContent>
    </Dialog>
  );
}
